import clsx from "clsx";
import { type ComponentProps, useMemo } from "react";

import type { StrapiMedia } from "@shared/content-types/schemas/media.schema";
import type { TitleWithIconType } from "@shared/content-types/types";
import { BrandIcon } from "@shared/ui/brand-icon";
import { Image } from "@shared/ui/image";
import { Body, Heading } from "@shared/ui/typography";
import {
  ApplyReplacements,
  getTitleWithIconData,
} from "@shared/ui/typography/apply-replacements-to-text";

import { useStoriesAnimation } from "@/_pages/home/ui/main-stories-section/hooks/use-stories-animation";

import s from "./main-stories-section.module.scss";

type StoryImageType = {
  image: StrapiMedia;
};

export type mainStoriesSectionProps = Omit<ComponentProps<"div">, "title"> & {
  className?: string;
  title: TitleWithIconType;
  images: StoryImageType[];
  subtitle?: string | null;
};

export const MainStoriesSection = (props: mainStoriesSectionProps) => {
  const { className, images, title, subtitle } = props;

  const titleSetting = useMemo(() => {
    return getTitleWithIconData(title);
  }, [title]);

  const { $root, $images, $icons } = useStoriesAnimation(() => {});

  return (
    <section className={clsx(s.root, className)} ref={$root}>
      <div className={s.content}>
        <div className={clsx(s.center, { [s.centeredText]: !!subtitle })}>
          {subtitle && (
            <Body
              size="uppercase-small"
              weight="semiBold"
              className={s.subtitle}
            >
              {subtitle}
            </Body>
          )}
          <Heading
            level={3}
            tag="h2"
            className={s.title}
            aria-label={titleSetting.text}
          >
            <ApplyReplacements
              text={titleSetting.text}
              settings={titleSetting.settings}
            />
          </Heading>
        </div>
      </div>
      <div className={s.inner}>
        <div className={s.decoration}>
          {images?.map((item, i) => {
            const image = item?.image;
            if (!image?.url) return null;

            return (
              <Image
                ref={(ref: HTMLDivElement | null) => {
                  $images.current[i] = ref;
                }}
                className={s.image}
                key={image?.documentId ?? i}
                src={image.url}
                alt={image?.alternativeText ?? "section image"}
                objectFit="cover"
                height="100%"
              />
            );
          })}
          <BrandIcon
            ref={(ref: HTMLDivElement | null) => {
              $icons.current[0] = ref;
            }}
            fillColor="violet"
            name="sur"
            className={s.icon}
          />
          <BrandIcon
            ref={(ref: HTMLDivElement | null) => {
              $icons.current[1] = ref;
            }}
            fillColor="azure"
            name="plus"
            className={s.icon}
          />
        </div>
      </div>
    </section>
  );
};

MainStoriesSection.displayName = "MainStoriesSection";
